import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Generic hover/focus popover. Portals the panel to <body> with viewport-aware
 * placement (flips above when it would overflow), a short close grace so the
 * pointer can travel into the panel, and stopPropagation on the anchor so a tap
 * inside a clickable row doesn't navigate. Mechanics lifted from
 * ScheduleWeatherChip; one panel mounts at a time (per instance).
 */
export default function HoverPopover({
  anchor,
  children,
  width = 280,
}: {
  anchor: ReactNode;
  children: ReactNode;
  width?: number;
}) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const h = panelRef.current?.getBoundingClientRect().height ?? 0;
      const margin = 8;
      const left = Math.max(margin, Math.min(r.left + r.width / 2 - width / 2, window.innerWidth - width - margin));
      const below = r.bottom + 8;
      const above = r.top - h - 8;
      const top = below + h <= window.innerHeight - margin || above < margin ? below : above;
      setPos({ top, left });
    };
    const raf = window.requestAnimationFrame(place);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, width]);

  useEffect(() => () => { if (closeTimer.current) window.clearTimeout(closeTimer.current); }, []);

  const show = () => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 100);
  };

  return (
    <>
      <span
        ref={anchorRef}
        style={{ display: "inline-flex", maxWidth: "100%" }}
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        onFocus={show}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) scheduleClose(); }}
        onClick={(e) => { e.stopPropagation(); show(); }}
      >
        {anchor}
      </span>
      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          onMouseEnter={show}
          onMouseLeave={scheduleClose}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: `${pos.top}px`,
            left: `${pos.left}px`,
            width: `${width}px`,
            zIndex: 200,
            background: "rgba(10, 14, 22, 0.98)",
            border: "1px solid var(--rule)",
            borderRadius: "10px",
            boxShadow: "0 18px 50px rgba(2, 6, 23, 0.6)",
            padding: "0.7rem 0.8rem",
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
